import type MapCoordinate from '@/types/MapCoordinate.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'

export const updateCoordinateShips = (
  mapCoordinate: MapCoordinate,
  totalPositions: number,
  position: number,
  zoom: number
) => {
  let scale = 0
  let positionType = 'space'
  let zPosition = -0.1 // Default z position behind planets (negative value)

  // Type needs to be checked as a number
  const coordType = Number(mapCoordinate.type)
  if (coordType === 0) {
    //Space
    positionType = 'space'
  } else if (coordType === 1) {
    //Planet
    positionType = 'planet'
    zPosition = -0.3 // Make ships appear behind planets
  } else if (coordType === 2) {
    //Star
    positionType = 'planet'
    zPosition = -0.3 // Make ships appear behind stars too
  }

  // Use fixed scale values at specific zoom levels for better appearance
  if (zoom === zoomFactor.Planet) {
    scale = 1.5 // Increased from 1.0 for better visibility at planet level
  } else if (zoom === zoomFactor.Solar) {
    scale = 3.0 // Increased from 2.0 for better visibility at solar level
  } else if (zoom === zoomFactor.Local) {
    scale = 10.0 // Much larger at local level (increased from 6.0)
  } else if (zoom === zoomFactor.Sector) {
    scale = 12.0 // Larger at sector level (increased from 8.0)
  } else if (zoom === zoomFactor.Quadrant) {
    scale = 0 // Hidden at quadrant level
  } else if (zoom === zoomFactor.Max) {
    scale = 0 // Hidden at max level (most zoomed out)
  } else {
    // For intermediate zoom levels, calculate scale based on nearest preset zoom levels
    if (zoom > zoomFactor.Solar && zoom < zoomFactor.Planet) {
      // Between Solar and Planet
      const percent = (zoom - zoomFactor.Solar) / (zoomFactor.Planet - zoomFactor.Solar)
      scale = 3.0 + (1.5 - 3.0) * percent
    } else if (zoom > zoomFactor.Local && zoom < zoomFactor.Solar) {
      // Between Local and Solar
      const percent = (zoom - zoomFactor.Local) / (zoomFactor.Solar - zoomFactor.Local)
      scale = 10.0 + (3.0 - 10.0) * percent
    } else if (zoom > zoomFactor.Sector && zoom < zoomFactor.Local) {
      // Between Sector and Local
      const percent = (zoom - zoomFactor.Sector) / (zoomFactor.Local - zoomFactor.Sector)
      scale = 12.0 + (10.0 - 12.0) * percent
    } else if (zoom > zoomFactor.Quadrant && zoom < zoomFactor.Sector) {
      // Between Quadrant and Sector
      const percent = (zoom - zoomFactor.Quadrant) / (zoomFactor.Sector - zoomFactor.Quadrant)
      scale = 0 + (12.0 - 0) * percent
    }
  }

  // Get coordinates based on zoom level and position type
  let planetCoords = { x: mapCoordinate.xPos, y: mapCoordinate.yPos }
  let solarCoords = { x: mapCoordinate.xPos, y: mapCoordinate.yPos }
  let localCoords = { x: mapCoordinate.xPos, y: mapCoordinate.yPos }
  // Define positions for larger zoom levels
  let sectorCoords = { x: mapCoordinate.xPos, y: mapCoordinate.yPos }
  let quadrantCoords = { x: mapCoordinate.xPos, y: mapCoordinate.yPos }
  let finalCoords = { x: mapCoordinate.xPos, y: mapCoordinate.yPos }

  if (positionType === 'planet') {
    planetCoords = positionPlanetZoomPlanet(
      mapCoordinate.xPos,
      mapCoordinate.yPos,
      totalPositions,
      position
    )
    solarCoords = positionPlanetZoomSolar(
      mapCoordinate.xPos,
      mapCoordinate.yPos,
      totalPositions,
      position
    )
    localCoords = positionPlanetZoomLocal(
      mapCoordinate.xPos,
      mapCoordinate.yPos,
      totalPositions,
      position
    )

    // For larger zoom levels, positions should converge back to the center point
    // but with greater distance from the planet
    sectorCoords = {
      x: mapCoordinate.xPos,
      y: mapCoordinate.yPos - 5 // Further away from planet at sector level
    }

    quadrantCoords = {
      x: mapCoordinate.xPos,
      y: mapCoordinate.yPos - 6 // Even further at quadrant level
    }

    // Interpolate between coordinates based on zoom level
    if (zoom === zoomFactor.Planet) {
      finalCoords = planetCoords
    } else if (zoom === zoomFactor.Solar) {
      finalCoords = solarCoords
    } else if (zoom === zoomFactor.Local) {
      finalCoords = localCoords
    } else if (zoom === zoomFactor.Sector) {
      finalCoords = sectorCoords
    } else if (zoom === zoomFactor.Quadrant) {
      finalCoords = quadrantCoords
    } else if (zoom > zoomFactor.Solar && zoom < zoomFactor.Planet) {
      // Interpolate between Solar and Planet
      const percent = (zoom - zoomFactor.Solar) / (zoomFactor.Planet - zoomFactor.Solar)
      finalCoords.x = solarCoords.x + (planetCoords.x - solarCoords.x) * percent
      finalCoords.y = solarCoords.y + (planetCoords.y - solarCoords.y) * percent
    } else if (zoom > zoomFactor.Local && zoom < zoomFactor.Solar) {
      // Interpolate between Local and Solar
      const percent = (zoom - zoomFactor.Local) / (zoomFactor.Solar - zoomFactor.Local)
      finalCoords.x = localCoords.x + (solarCoords.x - localCoords.x) * percent
      finalCoords.y = localCoords.y + (solarCoords.y - localCoords.y) * percent
    } else if (zoom > zoomFactor.Sector && zoom < zoomFactor.Local) {
      // Interpolate between Sector and Local
      const percent = (zoom - zoomFactor.Sector) / (zoomFactor.Local - zoomFactor.Sector)
      finalCoords.x = sectorCoords.x + (localCoords.x - sectorCoords.x) * percent
      finalCoords.y = sectorCoords.y + (localCoords.y - sectorCoords.y) * percent
    } else if (zoom > zoomFactor.Quadrant && zoom < zoomFactor.Sector) {
      // Interpolate between Quadrant and Sector
      const percent = (zoom - zoomFactor.Quadrant) / (zoomFactor.Sector - zoomFactor.Quadrant)
      finalCoords.x = quadrantCoords.x + (sectorCoords.x - quadrantCoords.x) * percent
      finalCoords.y = quadrantCoords.y + (sectorCoords.y - quadrantCoords.y) * percent
    }
  } else if (positionType === 'space') {
    const planetSpaceCoords = positionSpacePlanet(
      mapCoordinate.xPos,
      mapCoordinate.yPos,
      totalPositions,
      position
    )
    const solarSpaceCoords = positionSpaceSolar(
      mapCoordinate.xPos,
      mapCoordinate.yPos,
      totalPositions,
      position
    )

    // For space objects at larger zoom levels
    const sectorSpaceCoords = {
      x: mapCoordinate.xPos,
      y: mapCoordinate.yPos
    }

    const quadrantSpaceCoords = {
      x: mapCoordinate.xPos,
      y: mapCoordinate.yPos
    }

    if (zoom === zoomFactor.Planet) {
      finalCoords = planetSpaceCoords
    } else if (zoom === zoomFactor.Solar) {
      finalCoords = solarSpaceCoords
    } else if (zoom === zoomFactor.Sector) {
      finalCoords = sectorSpaceCoords
    } else if (zoom === zoomFactor.Quadrant) {
      finalCoords = quadrantSpaceCoords
    } else if (zoom > zoomFactor.Solar && zoom < zoomFactor.Planet) {
      // Interpolate between Solar and Planet spaces
      const percent = (zoom - zoomFactor.Solar) / (zoomFactor.Planet - zoomFactor.Solar)
      finalCoords.x = solarSpaceCoords.x + (planetSpaceCoords.x - solarSpaceCoords.x) * percent
      finalCoords.y = solarSpaceCoords.y + (planetSpaceCoords.y - solarSpaceCoords.y) * percent
    } else if (zoom > zoomFactor.Local && zoom < zoomFactor.Solar) {
      // Interpolate between Local and Solar for space objects
      // In this case, we'll use solarSpaceCoords as both endpoints to avoid jumps
      finalCoords = solarSpaceCoords
    } else if (zoom > zoomFactor.Sector && zoom < zoomFactor.Local) {
      // Interpolate between Sector and Local for space objects
      const percent = (zoom - zoomFactor.Sector) / (zoomFactor.Local - zoomFactor.Sector)
      finalCoords.x = sectorSpaceCoords.x + (solarSpaceCoords.x - sectorSpaceCoords.x) * percent
      finalCoords.y = sectorSpaceCoords.y + (solarSpaceCoords.y - sectorSpaceCoords.y) * percent
    } else if (zoom > zoomFactor.Quadrant && zoom < zoomFactor.Sector) {
      // Interpolate between Quadrant and Sector for space objects
      const percent = (zoom - zoomFactor.Quadrant) / (zoomFactor.Sector - zoomFactor.Quadrant)
      finalCoords.x =
        quadrantSpaceCoords.x + (sectorSpaceCoords.x - quadrantSpaceCoords.x) * percent
      finalCoords.y =
        quadrantSpaceCoords.y + (sectorSpaceCoords.y - quadrantSpaceCoords.y) * percent
    } else {
      finalCoords = solarSpaceCoords
    }
  }

  return {
    x: finalCoords.x,
    y: finalCoords.y,
    z: zPosition,
    scale: scale
  }
}

const positionPlanetZoomPlanet = (
  x: number,
  y: number,
  totalPositions: number,
  position: number
): { x: number; y: number } => {
  const middlePos = {
    x: x,
    y: y - 1.5
  }
  const leftPos = {
    x: x - 0.3,
    y: y - 1.5
  }
  const rightPos = {
    x: x + 0.3,
    y: y - 1.5
  }
  const farLeftPos = {
    x: x - 0.6,
    y: y - 1.5
  }
  const farRightPos = {
    x: x + 0.6,
    y: y - 1.5
  }

  if (totalPositions === 1) {
    return middlePos
  } else if (totalPositions === 2) {
    if (position === 1) {
      return leftPos
    } else if (position === 2) {
      return rightPos
    }
  } else if (totalPositions === 3) {
    if (position === 1) {
      return farLeftPos
    } else if (position === 2) {
      return middlePos
    } else if (position === 3) {
      return farRightPos
    }
  } else if (totalPositions === 4) {
    if (position === 1) {
      return farLeftPos
    } else if (position === 2) {
      return leftPos
    } else if (position === 3) {
      return rightPos
    } else if (position === 4) {
      return farRightPos
    }
  } else if (totalPositions === 5) {
    if (position === 1) {
      return farLeftPos
    } else if (position === 2) {
      return leftPos
    } else if (position === 3) {
      return middlePos
    } else if (position === 4) {
      return rightPos
    } else if (position === 5) {
      return farRightPos
    }
  }

  // Default fallback
  return middlePos
}

const positionPlanetZoomSolar = (
  x: number,
  y: number,
  totalPositions: number,
  position: number
): { x: number; y: number } => {
  const middlePos = {
    x: x,
    y: y - 1.5
  }
  const leftPos = {
    x: x - 0.3,
    y: y - 1.5
  }
  const rightPos = {
    x: x + 0.3,
    y: y - 1.5
  }
  const farLeftPos = {
    x: x - 0.6,
    y: y - 1.5
  }
  const farRightPos = {
    x: x + 0.6,
    y: y - 1.5
  }

  if (totalPositions === 1) {
    return middlePos
  } else if (totalPositions === 2) {
    if (position === 1) {
      return leftPos
    } else if (position === 2) {
      return rightPos
    }
  } else if (totalPositions === 3) {
    if (position === 1) {
      return farLeftPos
    } else if (position === 2) {
      return middlePos
    } else if (position === 3) {
      return farRightPos
    }
  } else if (totalPositions === 4) {
    if (position === 1) {
      return farLeftPos
    } else if (position === 2) {
      return leftPos
    } else if (position === 3) {
      return rightPos
    } else if (position === 4) {
      return farRightPos
    }
  } else if (totalPositions === 5) {
    if (position === 1) {
      return farLeftPos
    } else if (position === 2) {
      return leftPos
    } else if (position === 3) {
      return middlePos
    } else if (position === 4) {
      return rightPos
    } else if (position === 5) {
      return farRightPos
    }
  }
  // Default fallback
  return middlePos
}

const positionPlanetZoomLocal = (
  x: number,
  y: number,
  totalPositions: number,
  position: number
): { x: number; y: number } => {
  const middlePos = {
    x: x - 0.5,
    y: y - 4
  }
  const leftPos = {
    x: x - 1,
    y: y - 4
  }
  const rightPos = {
    x: x + 1,
    y: y - 4
  }
  const farLeftPos = {
    x: x - 2,
    y: y - 4
  }
  const farRightPos = {
    x: x + 2,
    y: y - 4
  }

  if (totalPositions === 1) {
    return middlePos
  } else if (totalPositions === 2) {
    if (position === 1) {
      return leftPos
    } else if (position === 2) {
      return rightPos
    }
  } else if (totalPositions === 3) {
    if (position === 1) {
      return farLeftPos
    } else if (position === 2) {
      return middlePos
    } else if (position === 3) {
      return farRightPos
    }
  } else if (totalPositions === 4) {
    if (position === 1) {
      return farLeftPos
    } else if (position === 2) {
      return leftPos
    } else if (position === 3) {
      return rightPos
    } else if (position === 4) {
      return farRightPos
    }
  } else if (totalPositions === 5) {
    if (position === 1) {
      return farLeftPos
    } else if (position === 2) {
      return leftPos
    } else if (position === 3) {
      return middlePos
    } else if (position === 4) {
      return rightPos
    } else if (position === 5) {
      return farRightPos
    }
  }
  // Default fallback
  return middlePos
}

const positionSpacePlanet = (
  x: number,
  y: number,
  totalPositions: number,
  position: number
): { x: number; y: number } => {
  const firstPos = {
    x: x,
    y: y
  }
  const secondPos = {
    x: x - 0.25,
    y: y - 0.25
  }
  const thirdPos = {
    x: x + 0.25,
    y: y - 0.25
  }
  const fourthPos = {
    x: x - 0.25,
    y: y + 0.25
  }
  const fifthPos = {
    x: x + 0.25,
    y: y + 0.25
  }
  if (totalPositions === 1) {
    return firstPos
  } else if (totalPositions === 2) {
    if (position === 1) {
      return secondPos
    } else if (position === 2) {
      return fourthPos
    }
  } else if (totalPositions === 3) {
    if (position === 1) {
      return secondPos
    } else if (position === 2) {
      return thirdPos
    } else if (position === 3) {
      return fourthPos
    }
  } else if (totalPositions === 4) {
    if (position === 1) {
      return secondPos
    } else if (position === 2) {
      return thirdPos
    } else if (position === 3) {
      return fourthPos
    } else if (position === 4) {
      return fifthPos
    }
  } else if (totalPositions === 5) {
    if (position === 1) {
      return firstPos
    } else if (position === 2) {
      return secondPos
    } else if (position === 3) {
      return thirdPos
    } else if (position === 4) {
      return fourthPos
    } else if (position === 5) {
      return fifthPos
    }
  }
  // Default fallback
  return firstPos
}

const positionSpaceSolar = (
  x: number,
  y: number,
  totalPositions: number,
  position: number
): { x: number; y: number } => {
  const firstPos = {
    x: x,
    y: y
  }
  const secondPos = {
    x: x - 0.5,
    y: y - 0.5
  }
  const thirdPos = {
    x: x + 0.5,
    y: y - 0.5
  }
  const fourthPos = {
    x: x - 0.5,
    y: y + 0.5
  }
  const fifthPos = {
    x: x + 0.5,
    y: y + 0.5
  }
  if (totalPositions === 1) {
    return firstPos
  } else if (totalPositions === 2) {
    if (position === 1) {
      return secondPos
    } else if (position === 2) {
      return fourthPos
    }
  } else if (totalPositions === 3) {
    if (position === 1) {
      return secondPos
    } else if (position === 2) {
      return thirdPos
    } else if (position === 3) {
      return fourthPos
    }
  } else if (totalPositions === 4) {
    if (position === 1) {
      return secondPos
    } else if (position === 2) {
      return thirdPos
    } else if (position === 3) {
      return fourthPos
    } else if (position === 4) {
      return fifthPos
    }
  } else if (totalPositions === 5) {
    if (position === 1) {
      return firstPos
    } else if (position === 2) {
      return secondPos
    } else if (position === 3) {
      return thirdPos
    } else if (position === 4) {
      return fourthPos
    } else if (position === 5) {
      return fifthPos
    }
  }
  // Default fallback
  return firstPos
}
